import { useEffect, useRef, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";

const CONTAINER_PADDING = 20;
const INTERSECTION_ROOT_MARGIN = "200px";
const PAGE_MARGIN_BOTTOM = "20px";
const RESIZE_DEBOUNCE_TIME = 150;
const RESIZE_OBSERVER_DEBOUNCE_TIME = 200;
const DEFAULT_CONTAINER_WIDTH = 800;
const DEFAULT_CONTAINER_HEIGHT = 600;
const CONTAINER_SIZE_RETRY_DELAY = 100;

interface UsePdfRendererOptions {
  pdf: pdfjsLib.PDFDocumentProxy | null;
  numPages: number | null;
  container: HTMLDivElement | null;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  enablePanzoom: boolean;
  onLoadingChange: (loading: boolean) => void;
}

export const usePdfRenderer = ({
  pdf,
  numPages,
  container,
  scrollContainerRef,
  enablePanzoom,
  onLoadingChange,
}: UsePdfRendererOptions) => {
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const renderTasksRef = useRef<Map<number, pdfjsLib.RenderTask>>(new Map());
  const pagePlaceholdersRef = useRef<Map<number, HTMLDivElement>>(new Map());
  const renderedPagesRef = useRef<Set<number>>(new Set());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const initializingRef = useRef<boolean>(false);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanupRender = useCallback(() => {
    renderTasksRef.current.forEach((task) => task.cancel());
    renderTasksRef.current.clear();
    canvasRefs.current.forEach((canvas) => {
      if (canvas.parentNode) {
        canvas.parentNode.removeChild(canvas);
      }
    });
    canvasRefs.current.clear();
    pagePlaceholdersRef.current.forEach((placeholder) => {
      if (placeholder.parentNode) {
        placeholder.parentNode.removeChild(placeholder);
      }
    });
    pagePlaceholdersRef.current.clear();
    renderedPagesRef.current.clear();
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    initializingRef.current = false;
  }, []);

  const getContainerWidth = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const containerElement = scrollContainer || container;
    if (!containerElement) return 0;
    return containerElement.clientWidth - CONTAINER_PADDING * 2;
  }, [container, scrollContainerRef]);

  const getContainerHeight = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    const containerElement = scrollContainer || container;
    if (!containerElement) return 0;
    return containerElement.clientHeight - CONTAINER_PADDING * 2;
  }, [container, scrollContainerRef]);

  const renderPage = useCallback(
    async (pageNum: number, containerWidth: number, totalPages: number) => {
      if (!pdf || renderedPagesRef.current.has(pageNum)) return;

      try {
        renderedPagesRef.current.add(pageNum);
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1 });
        
        let actualContainerWidth = getContainerWidth();
        if (actualContainerWidth <= 0) {
          actualContainerWidth = containerWidth;
        }
        if (actualContainerWidth <= 0) {
          await new Promise(resolve => requestAnimationFrame(resolve));
          actualContainerWidth = getContainerWidth();
        }
        if (actualContainerWidth <= 0) {
          actualContainerWidth = DEFAULT_CONTAINER_WIDTH;
        }
        
        const containerHeight = getContainerHeight();
        const pdfAspectRatio = viewport.width / viewport.height;
        const containerAspectRatio = actualContainerWidth / (containerHeight || DEFAULT_CONTAINER_HEIGHT);
        
        let scale: number;
        if (pdfAspectRatio > containerAspectRatio && containerHeight > 0) {
          const heightScale = containerHeight / viewport.height;
          const widthScale = actualContainerWidth / viewport.width;
          scale = Math.min(heightScale, widthScale);
        } else {
          scale = actualContainerWidth / viewport.width;
        }
        
        const scaledViewport = page.getViewport({ scale });

        const placeholder = pagePlaceholdersRef.current.get(pageNum);
        if (!placeholder) return;

        const canvas = document.createElement("canvas");
        canvasRefs.current.set(pageNum, canvas);

        const dpr = window.devicePixelRatio || 1;
        canvas.width = scaledViewport.width * dpr;
        canvas.height = scaledViewport.height * dpr;

        const context = canvas.getContext("2d");
        if (!context) return;

        context.scale(dpr, dpr);

        canvas.style.display = "block";
        canvas.style.margin = "0 auto";
        canvas.style.marginBottom = pageNum < totalPages ? PAGE_MARGIN_BOTTOM : "0";
        canvas.style.maxWidth = "100%";
        canvas.style.width = `${scaledViewport.width}px`;
        canvas.style.height = `${scaledViewport.height}px`;

        placeholder.appendChild(canvas);

        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
        } as Parameters<typeof page.render>[0];

        const renderTask = page.render(renderContext);
        renderTasksRef.current.set(pageNum, renderTask);
        await renderTask.promise;
        
        placeholder.style.visibility = "visible";
      } catch (err: any) {
        renderedPagesRef.current.delete(pageNum);
        if (err?.name !== "RenderingCancelledException") {
          console.error(`ページ${pageNum}のレンダリングエラー:`, err);
        }
      }
    },
    [pdf, getContainerWidth, getContainerHeight]
  );

  useEffect(() => {
    if (!pdf || numPages === null || !container) return;

    let cancelled = false;

    const initializeRenderer = async () => {
      if (cancelled || initializingRef.current) return;

      try {
        initializingRef.current = true;
        cleanupRender();

        await new Promise(resolve => requestAnimationFrame(resolve));
        
        let containerWidth = getContainerWidth();
        if (containerWidth <= 0) {
          await new Promise(resolve => setTimeout(resolve, CONTAINER_SIZE_RETRY_DELAY));
          containerWidth = getContainerWidth();
        }
        if (containerWidth <= 0) {
          containerWidth = DEFAULT_CONTAINER_WIDTH;
        }

        if (numPages === 0) {
          return;
        }

        const firstPage = await pdf.getPage(1);
        const firstViewport = firstPage.getViewport({ scale: 1 });
        const containerHeight = getContainerHeight();
        const pdfAspectRatio = firstViewport.width / firstViewport.height;
        const containerAspectRatio = containerWidth / (containerHeight || DEFAULT_CONTAINER_HEIGHT);
        
        let firstScale: number;
        if (pdfAspectRatio > containerAspectRatio && containerHeight > 0) {
          const heightScale = containerHeight / firstViewport.height;
          const widthScale = containerWidth / firstViewport.width;
          firstScale = Math.min(heightScale, widthScale);
        } else {
          firstScale = containerWidth / firstViewport.width;
        }
        const firstScaledViewport = firstPage.getViewport({ scale: firstScale });

        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          if (cancelled) {
            return;
          }

          const placeholder = document.createElement("div");
          placeholder.setAttribute("data-page-number", pageNum.toString());
          placeholder.style.minHeight = `${firstScaledViewport.height}px`;
          placeholder.style.marginBottom = pageNum < numPages ? PAGE_MARGIN_BOTTOM : "0";
          placeholder.style.display = "flex";
          placeholder.style.justifyContent = "center";
          placeholder.style.alignItems = "flex-start";
          placeholder.style.visibility = "hidden";

          pagePlaceholdersRef.current.set(pageNum, placeholder);
          container.appendChild(placeholder);
        }

        if (cancelled) {
          return;
        }

        const observerRoot = enablePanzoom ? container : scrollContainer;

        observerRef.current = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const pageNum = parseInt(entry.target.getAttribute("data-page-number") || "0", 10);
                if (pageNum > 0 && !renderedPagesRef.current.has(pageNum)) {
                  renderPage(pageNum, containerWidth, numPages);
                }
              }
            });
          },
          {
            root: observerRoot,
            rootMargin: INTERSECTION_ROOT_MARGIN,
            threshold: 0.01,
          }
        );

        pagePlaceholdersRef.current.forEach((placeholder) => {
          observerRef.current?.observe(placeholder);
        });

        const visiblePages = Array.from(pagePlaceholdersRef.current.keys()).slice(0, 3);
        let firstPageRendered = false;
        for (const pageNum of visiblePages) {
          if (cancelled) break;
          await renderPage(pageNum, containerWidth, numPages);
          if (!firstPageRendered) {
            firstPageRendered = true;
            // 最初のページがレンダリングされた後、2フレーム待機します。
            // これにより、ブラウザがキャンバスを描画し、レイアウトが安定するのを保証し、
            // ローディングインジケーターを非表示にする際のちらつきを防ぎます。
            await new Promise(resolve => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  resolve(undefined);
                });
              });
            });
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("PDFレンダリング初期化エラー:", err);
        }
      } finally {
        if (!cancelled) {
          initializingRef.current = false;
          onLoadingChange(false);
        }
      }
    };

    initializeRenderer();

    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        if (pdf && numPages !== null && container && !cancelled && !initializingRef.current) {
          initializeRenderer();
        }
      }, RESIZE_DEBOUNCE_TIME);
    };

    window.addEventListener("resize", handleResize);

    const scrollContainer = scrollContainerRef.current;
    const containerElement = scrollContainer || container;
    if (containerElement) {
      resizeObserverRef.current = new ResizeObserver(() => {
        if (resizeTimeoutRef.current) {
          clearTimeout(resizeTimeoutRef.current);
        }
        resizeTimeoutRef.current = setTimeout(() => {
          if (pdf && numPages !== null && container && !cancelled && !initializingRef.current) {
            const currentWidth = getContainerWidth();
            const currentHeight = getContainerHeight();
            if (currentWidth > 0 && currentHeight > 0) {
              initializeRenderer();
            }
          }
        }, RESIZE_OBSERVER_DEBOUNCE_TIME);
      });
      resizeObserverRef.current.observe(containerElement);
    }

    return () => {
      cancelled = true;
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      cleanupRender();
      window.removeEventListener("resize", handleResize);
    };
  }, [pdf, numPages, container, scrollContainerRef, enablePanzoom, onLoadingChange, cleanupRender, renderPage, getContainerWidth, getContainerHeight]);

  return { cleanupRender };
};

